'use client';
import Image from "next/image";
import {useTranslations} from 'next-intl';
import {formatProductReviewDate} from '@/libs/date.util';
import defaultPhoto from '@/assets/img/user/default.png';
import ProductDetailsReviewsAdd from '@/components/shared/products/ProductDetailsReviewsAdd';
import {useState} from 'react';
import {useBaseMutation} from '@/libs/api/baseRequests';
import ProductReviewReplies from '@/components/shared/products/ProductReviewReplies';
import {ADD_PRODUCT_REVIEW_REPLY_QUERY, DELETE_PRODUCT_REVIEW_QUERY} from '@/graphql/mutations';
import DeleteBaseModal from '@/components/shared/modals/DeleteBaseModal';
import {hideDeleteModal} from '@/libs/jquery.util';
import ProductDetailsReviewsEdit from '@/components/shared/products/ProductDetailsReviewsEdit';
import ProductReviewRepliesEditDelete from '@/components/shared/products/ProductReviewRepliesEditDelete';
import {useUserContext} from '@/providers/UserContext';
import {Link} from "@/i18n/routing";
import {CALLBACK_URL_PARAM, ClientTypes, LOGIN_ROUTE_NAME} from '@/libs/constants/common.constants';
import {usePathname} from '@/i18n/routing';

export default function ProductDetailsReviews({ reviewComputed, reviews, productId }) {
  const t = useTranslations('product');
  const d = useTranslations('date.months');
  
  const { user } = useUserContext();
  const pathname = usePathname();
  
  const [clientReviews, setClientReviews] = useState(reviews ?? []);
  const [deleteModal, setDeleteModal] = useState(null);
  const [replyFieldIndex, setReplyFieldIndex] = useState(null);
  const [isReplySubmitting, setReplySubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [newRepliesObj, setNewRepliesObj] = useState({});
  const [currentReview, setCurrentReview] = useState(null);
  const [isEditingReview, setEditingReview] = useState(false);
  const [deleteAction, setDeleteAction] = useState(() => () => {});
  
  const { mutate: addReply } = useBaseMutation(ADD_PRODUCT_REVIEW_REPLY_QUERY, {
    toastOnComplete: { toast: true, message: t('replyAdded') }
  });
  const { mutate: deleteReview } = useBaseMutation(DELETE_PRODUCT_REVIEW_QUERY, {
    toastOnComplete: { toast: true, message: t('reviewDeleted') }
  });
  
  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
  };
  
  const onReplyClick = (idx) => {
		if (idx === replyFieldIndex) return;
    
    setReplyFieldIndex(idx);
    setReplyText('');
  };
  
  const onCancelReplyClick = () => {
    setReplyText('');
    setReplyFieldIndex(null);
  };
  
  const onReplySubmit = async (idx, id) => {
    setReplySubmitting(true);
    const result = await addReply({
      variables: {
        productReviewId: id,
        comment: replyText,
      }
    });
    if (result.data) {
      setNewRepliesObj(prevState => ({
        ...prevState,
        [idx]: prevState[idx]
            ? [...prevState[idx], result.data.createProductReviewReply]
            : [result.data.createProductReviewReply]
      }));
      setReplyText('');
      setReplyFieldIndex(null);
    }
    setReplySubmitting(false);
  }
  
  const removeLocalReplies = (idx) => {
    if (Array.isArray(newRepliesObj[idx]) && newRepliesObj[idx].length > 0) {
      setNewRepliesObj(prevState => ({
        ...prevState,
        [idx]: []
      }));
    }
  }
  
  const onReviewEditClick = (idx, review) => {
    setCurrentReview({idx, review});
    setEditingReview(true);
  }
  
  const onReviewEditCancel = () => {
    setEditingReview(false);
    setCurrentReview(null);
  }
  
  const handleOpenDeleteModal = (type, onDelete) => {
    setDeleteModal(type);
    setDeleteAction(() => onDelete);
  };
  
  const onReviewDelete = async (idx, id, setDeleting) => {
    setDeleting(true);
    const result = await deleteReview({
      variables: { id }
    });
    if (result.data) {
      setClientReviews(prev => [
        ...prev.slice(0, idx),
        ...prev.slice(idx + 1)
      ]);
    }
    hideDeleteModal();
    setDeleting(false);
  }
  
  const onDeleteAction = async (setDeleting) => {
    if (deleteAction) {
      await deleteAction(setDeleting);
    }
		setDeleteModal(null);
  }
  
  return (
    <div className="ltn__shop-details-tab-content-inner">
      <h4 className="title-2">{t('customerReviews')}</h4>
      {
        reviewComputed ? (
            <div className="product-ratting" title={reviewComputed.average}>
              <ul className="rating-large">
                {
                  reviewComputed.stars.map((icon, i) => (
                      <li key={i}>
                        <i className={icon}></i>
                      </li>
                  ))
                }
                <li className="review-total ms-2">
                  ( {reviewComputed.average} • {t('countReviews', {count: reviewComputed.count})} )
                </li>
              </ul>
            </div>
        ) : (
            <div>{t('noReview')}</div>
        )
      }
      {
        !user ? (
            <div className="review-auth">
              {t.rich('authDescription', {
                a: (chunks) => (
                    <Link href={`${LOGIN_ROUTE_NAME}?${CALLBACK_URL_PARAM}=${encodeURIComponent(pathname)}`}>
                      {chunks}
                    </Link>
                )
              })}
            </div>
        ) : Array.isArray(clientReviews) && clientReviews.length && user?.email === clientReviews[0].client.email ? '' : (
            <ProductDetailsReviewsAdd
                productId={productId}
                setReviews={setClientReviews}
            />
        )
      }
      <hr className="my-4"/>
      {/* <!-- comment-area --> */}
      <div className="ltn__comment-area mb-30">
        <div className="ltn__comment-inner">
          <ul>
            {
              Array.isArray(clientReviews) && clientReviews.length ? (
                  clientReviews.map((review, idx) => (
                      <li key={idx} className="review-item">
                        <div className="ltn__comment-item clearfix">
                          <div className="ltn__commenter-img">
                            <Image
                                key={`${review.client.fullName}-${idx}`}
                                src={review.client.image ?? defaultPhoto}
                                alt={`${review.client.fullName}-${idx + 1}`}
                                placeholder="blur"
                                blurDataURL={review.client.image}
                                width={100}
                                height={100}
                                style={{height: 'auto', aspectRatio: 1, objectFit: 'cover'}}
                            />
                          </div>
                          <div className="ltn__commenter-comment">
                            {
                              !user ? '' : (
                                  <button
                                      className="btn btn-transparent reply-button"
                                      onClick={() => onReplyClick(idx)}
                                  >
                                    <i className="fas fa-reply me-2"></i>
                                    {t('reply').toUpperCase()}
                                  </button>
                              )
                            }
                            
                            <h6>
                              {review.client.fullName}
                              {
                                review.client.clientType === ClientTypes.ADMIN ? (
                                    <span className="client-review">
                                      ( {t('admin')} )
                                    </span>
                                ) : ''
                              }
                              {
                                review.client.email === user?.email ? (
                                    <span className="client-review">
                                      ( {t('yourReview')} )
                                    </span>
                                ) : ''
                              }
                            </h6>
                            {
                              isEditingReview && currentReview && currentReview.idx === idx ? '' : (
                                  <div className="product-ratting" title={review.value}>
                                    <ul>
                                      {
                                        [...Array(review.value)].map((_, i) => (
                                            <li key={`rat-f-${i}`}>
                                              <i className="fas fa-star"></i>
                                            </li>
                                        ))
                                      }
                                      {
                                        [...Array(5 - review.value)].map((_, i) => (
                                            <li key={`rat-n-${i}`}>
                                              <i className="far fa-star"></i>
                                            </li>
                                        ))
                                      }
                                      <li className="review-date">
                                        {formatProductReviewDate(review.createdAt, d)}
                                      </li>
                                    </ul>
                                  </div>
                              )
                            }
                          </div>
                          <div className="review-comment">
                            {
                              isEditingReview && currentReview && currentReview.idx === idx ? (
                                  <ProductDetailsReviewsEdit
                                      reviewIdx={currentReview.idx}
                                      review={currentReview.review}
                                      setReviews={setClientReviews}
                                      onReviewEditCancel={onReviewEditCancel}
                                  />
                              ) : (
                                  <>
                                    <p>{review.comment}</p>
                                    {
                                      review.client.email === user?.email ? (
                                          <>
                                            <button
                                                className="btn btn-transparent reply-button me-2 float-none"
                                                onClick={() => handleOpenDeleteModal('review', (setDeleting) => onReviewDelete(idx, review.id, setDeleting))}
                                                data-bs-toggle="modal"
                                                data-bs-target="#delete-base-modal"
                                            >
                                              <i className="fas fa-trash me-2"></i>
                                              {t('delete').toUpperCase()}
                                            </button>
                                            <button
                                                className="btn btn-transparent reply-button float-none"
                                                onClick={() => onReviewEditClick(idx, review)}
                                            >
                                              <i className="fas fa-edit me-2"></i>
                                              {t('edit').toUpperCase()}
                                            </button>
                                          </>
                                      ) : ''
                                    }
                                  </>
                              )
                            }
                            {
                              replyFieldIndex === idx ? (
                                  <div className="reply-comment-field">
                                    <textarea
                                        name="reply-comment"
                                        placeholder={t('addReply')}
																				value={replyText}
                                        disabled={isReplySubmitting}
																				onChange={handleReplyChange}
                                    />
                                    <button
                                        className="btn theme-btn-3 btn-effect-1 text-uppercase"
                                        disabled={isReplySubmitting}
                                        onClick={() => onCancelReplyClick()}
                                    >
                                      {t('cancel')}
                                    </button>
                                    <button
                                        className="btn theme-btn-1 btn-effect-1 text-uppercase"
                                        type="submit"
                                        disabled={isReplySubmitting}
                                        onClick={() => onReplySubmit(idx, review.id)}
                                    >
                                      {isReplySubmitting ? t('submitting') : t('submit')}
                                    </button>
                                  </div>
                              ) : ''
                            }
                            {
                              review.repliesCount?.count > 0 || (Array.isArray(newRepliesObj[idx]) && newRepliesObj[idx].length > 0) ? (
                                  <ProductReviewReplies
                                      reviewIdx={idx}
                                      reviewId={review.id}
                                      repliesCount={review.repliesCount?.count ?? 0}
                                      removeLocalReplies={removeLocalReplies}
                                      setDeleteModal={setDeleteModal}
                                      openDeleteModal={handleOpenDeleteModal}
                                  >
                                    {
                                      newRepliesObj[idx]?.length > 0 ? (
                                          newRepliesObj[idx].map((reply, repIdx) => (
                                              <li key={`pending-${repIdx}`} className="reply-item">
                                                <div className="ltn__comment-item clearfix">
                                                  <div className="ltn__commenter-img">
                                                    <Image
                                                        key={`${reply.client.fullName}-${repIdx}`}
                                                        src={reply.client.image ?? defaultPhoto}
                                                        alt={`${reply.client.fullName}-${repIdx + 1}`}
                                                        placeholder="blur"
                                                        blurDataURL={reply.client.fullName}
                                                        width={100}
                                                        height={100}
                                                        style={{height: 'auto', aspectRatio: 1, objectFit: 'cover'}}
                                                    />
                                                  </div>
                                                  <div className="ltn__commenter-comment">
                                                    <h6>
                                                      {reply.client.fullName}
                                                    </h6>
                                                    <div>
                                                      <div className="review-date">
                                                        {formatProductReviewDate(reply.createdAt, d)}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  <div className="reply-comment">
                                                    <ProductReviewRepliesEditDelete
                                                        currentReply={reply}
                                                        setNewRepliesObj={setNewRepliesObj}
                                                        replyIndex={repIdx}
                                                        reviewIndex={idx}
                                                        openDeleteModal={handleOpenDeleteModal}
                                                    />
                                                    {/*<p>{reply.comment}</p>*/}
                                                  </div>
                                                </div>
                                              </li>
                                          ))
                                      ) : ''
                                    }
                                  </ProductReviewReplies>
                              ) : ''
                            }
                          </div>
                        </div>
                      </li>
                  ))
              ) : ''
            }
          </ul>
          <DeleteBaseModal
              type={deleteModal}
              onDelete={onDeleteAction}
          />
        </div>
      </div>
    </div>
  );
};
